/** The ledger's pure half (plan §1.4): grouping the draft by entity, why Save is
 *  blocked, a change row's text excerpt, what the save confirmation names, and the
 *  instant catalog estimate shown while the preview loads. */
import {
  changeKey,
  isActionPath,
  type DraftEntry,
  type DraftRecordGroup,
  type DraftState,
  type RecordKey,
} from '../draft/changeSet';
import { answerFirst, missingChoices, type RaisedChoice } from '../editor/choices';
import type { RecordFormError } from '../editor/RecordEditorProvider';
import type {
  RecordAction,
  RecordEditorConfig,
  RecordEffect,
  RecordField,
  RecordPreview,
} from '../editor/types';

/** Rows shown per group before "Show all" (plan §1.6: 1–60 changes). */
export const GROUP_ROW_CAP = 12;

export interface LedgerRow {
  rec: RecordKey;
  path: string;
  entry: DraftEntry;
  field: RecordField | undefined;
  /** `"<entity>:<id>:<field or action>"`. */
  key: string;
  /** The record kind's label for the row (`config.rowLabel`); else the field's. */
  label?: string;
}

export interface LedgerGroup {
  id: string;
  title: string;
  note: string | null;
  rows: LedgerRow[];
}

/** Draft groups (one per record) merged by the record kind's grouping — a party
 *  member's booking and user are one group. `state` reaches the record kind's
 *  `groupOf` / `rowLabel` (a new row is named from the draft). */
export function ledgerGroups(
  groups: DraftRecordGroup[],
  groupOf: RecordEditorConfig['groupOf'],
  fieldForPath: (path: string, rec: RecordKey) => RecordField | undefined,
  state?: DraftState,
  rowLabel?: RecordEditorConfig['rowLabel'],
): LedgerGroup[] {
  const out: LedgerGroup[] = [];
  const byId = new Map<string, LedgerGroup>();
  for (const g of groups) {
    const meta = groupOf?.(g.rec, state) ?? { id: g.rec, title: `${g.entity} #${g.id}` };
    let group = byId.get(meta.id);
    if (!group) {
      group = { id: meta.id, title: meta.title, note: meta.note ?? null, rows: [] };
      byId.set(meta.id, group);
      out.push(group);
    }
    for (const { path, entry } of g.entries) {
      const field = fieldForPath(path, g.rec);
      const label = rowLabel && state ? rowLabel(g.rec, path, field, state) : undefined;
      group.rows.push({ rec: g.rec, path, entry, field, key: changeKey(g.rec, path), ...(label ? { label } : {}) });
    }
  }
  return out;
}

export interface BlockInput {
  count: number;
  rows: LedgerRow[];
  raised: RaisedChoice[];
  /** Per-row server / conflict / unparseable-input errors. */
  errorsFor: (rec: RecordKey, path: string) => string[];
  /** Why a row's value cannot be saved (the editor's `validateValue`: the field's widget's
   *  checks, else the generic ones). */
  validateValue: (field: RecordField, to: unknown, from: unknown) => string | null;
  /** Unparseable inputs anywhere (also on fields without a draft entry). */
  invalidInputs: Readonly<Record<string, string>>;
  formErrors: RecordFormError[];
  maxChanges?: number;
  descriptionLength: number;
  descriptionMaxLength?: number;
  /** A required description's minimum (trimmed); undefined = optional. */
  descriptionMinLength?: number;
}

export interface BlockState {
  /** Why Save is disabled; null = saveable. */
  reason: string | null;
  staleCount: number;
  invalidCount: number;
  missing: RaisedChoice[];
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** First blocking reason wins, in the order an operator resolves them. */
export function saveBlock(input: BlockInput): BlockState {
  const staleCount = input.rows.filter((r) => r.entry.stale).length;
  const goneCount = input.rows.filter((r) => r.entry.stale?.gone).length;
  const invalidKeys = new Set<string>(Object.keys(input.invalidInputs));
  for (const r of input.rows) {
    const bad =
      (r.field ? input.validateValue(r.field, r.entry.to, r.entry.from) : null) !== null ||
      input.errorsFor(r.rec, r.path).length > 0;
    if (bad) invalidKeys.add(`${r.rec}|${r.path}`);
  }
  const invalidCount = invalidKeys.size;
  const missing = missingChoices(input.raised);
  let reason: string | null = null;
  if (input.count === 0) reason = 'No changes to save';
  else if (goneCount > 0) {
    // A change to a record deleted elsewhere: only dropping it resolves it.
    reason = `${plural(goneCount, 'change is', 'changes are')} to something deleted elsewhere — drop ${goneCount === 1 ? 'it' : 'them'}`;
  } else if (staleCount > 0) {
    reason = `${plural(staleCount, 'field', 'fields')} changed on the server — choose Keep mine or Take theirs`;
  } else if (invalidCount > 0) reason = `Fix ${plural(invalidCount, 'invalid field', 'invalid fields')}`;
  else if (missing.length > 0) reason = answerFirst(missing);
  else if (input.maxChanges != null && input.count > input.maxChanges) {
    reason = `At most ${input.maxChanges} changes per save — save part of them first`;
  } else if (input.descriptionMinLength != null && input.descriptionLength < input.descriptionMinLength) {
    reason = `Add a description — at least ${input.descriptionMinLength} characters`;
  } else if (input.descriptionMaxLength != null && input.descriptionLength > input.descriptionMaxLength) {
    reason = `Shorten the description to ${input.descriptionMaxLength} characters`;
  } else {
    // A preview's refusal is about the body as it stands; the last save's is shown, but
    // Save stays a retry (`RecordFormError.source`).
    const blocking = input.formErrors.find((e) => e.source !== 'apply');
    if (blocking) reason = blocking.message;
  }
  return { reason, staleCount, invalidCount, missing };
}

/** Event statuses that do not reach anyone (the batch edit's vocabulary). */
const NOT_FIRING = new Set([
  'suppressed',
  'dropped_cancellation',
  'not_sent_transition',
  'not_sent_trip_ended',
  'via_lead_cancellation',
  'not_emitted',
]);

export function eventFires(status: string): boolean {
  return !NOT_FIRING.has(status);
}

/** Characters of unchanged text kept before the first difference. */
const EXCERPT_CONTEXT = 12;

/** A change row's before / after for text longer than `max`: both cut from just before
 *  where they first differ (at a word start, "…" for what is left out), so a long note
 *  edited near its end still shows WHAT changed rather than the shared opening. */
export function changeExcerpt(from: string, to: string, max: number): [string, string] {
  if (from.length <= max && to.length <= max) return [from, to];
  let same = 0;
  while (same < from.length && same < to.length && from[same] === to[same]) same += 1;
  let start = Math.max(0, same - EXCERPT_CONTEXT);
  if (start > 0) {
    const space = from.lastIndexOf(' ', start);
    // Back to the word's start — unless that gives back the whole context budget.
    start = space >= 0 && start - space <= EXCERPT_CONTEXT ? space + 1 : start;
  }
  const cut = (s: string) => {
    const head = start > 0 ? `…${s.slice(start)}` : s;
    return head.length > max ? `${head.slice(0, max - 1).trimEnd()}…` : head;
  };
  return [cut(from), cut(to)];
}

/** What the confirmation popover names: guest-visible events that fire, writes to
 *  outside systems, a cancellation. Empty = save without asking. */
export function confirmItems(preview: RecordPreview, confirmFollowUps: string[] = ['cancel_chain']): string[] {
  const out: string[] = [];
  const guest = [...new Set(preview.events.filter((e) => e.guest_visible && eventFires(e.status)).map((e) => e.code))];
  if (guest.length > 0) out.push(`Notifies the guest: ${guest.join(', ')}`);
  for (const step of preview.external) {
    out.push(step.key_labels?.length ? `${step.label} (${step.key_labels.join(', ')})` : step.label);
  }
  const risky = new Set(confirmFollowUps);
  for (const step of preview.follow_ups) if (risky.has(step.code)) out.push(step.label);
  for (const [code, effect] of Object.entries(preview.effects)) {
    if (risky.has(code) && !preview.follow_ups.some((s) => s.code === code)) out.push(effect.label);
  }
  return out;
}

export interface EstimateLine {
  code: string;
  label: string;
  kind: RecordEffect['kind'] | 'event';
  guestVisible?: boolean;
  /** Events: governed by the Emit · Suppress switch. */
  suppressible?: boolean;
}

/** The instant plan from the catalog: the effects every changed field and queued action
 *  declares, and the events the record kind expects. No keys — those come from the
 *  preview. */
export function catalogEstimate(
  rows: LedgerRow[],
  config: Pick<RecordEditorConfig, 'effects' | 'actions' | 'estimateEvents'>,
  state: DraftState,
  /** The event switches' values by option key (`guest_events: 'emit'`). */
  switches: Readonly<Record<string, string>> = {},
): { events: EstimateLine[]; effects: EstimateLine[] } {
  const codes = new Set<string>();
  const actionByKey = new Map<string, RecordAction>(config.actions.map((a) => [a.key, a]));
  for (const r of rows) {
    for (const c of r.field?.effects ?? []) codes.add(c);
    for (const c of r.field?.automatic ?? []) codes.add(c);
    if (isActionPath(r.path) && r.field?.action) {
      const a = actionByKey.get(r.field.action);
      for (const c of [...(a?.effects ?? []), ...(a?.automatic ?? [])]) codes.add(c);
    }
  }
  const effects = config.effects
    .filter((e) => codes.has(e.code))
    .map((e) => ({ code: e.code, label: e.label, kind: e.kind }));
  const seen = new Set<string>();
  const events: EstimateLine[] = [];
  for (const e of config.estimateEvents?.(state, switches) ?? []) {
    if (seen.has(e.code)) continue;
    seen.add(e.code);
    events.push({
      code: e.code,
      label: e.code,
      kind: 'event',
      guestVisible: e.guest_visible,
      suppressible: e.suppressible ?? true,
    });
  }
  return { events, effects };
}
